import type { Tier } from '@/policy/limits';

export type GuidedDenyReason = 'signup_required' | 'insufficient_credits' | 'unavailable';

export class GuidedLLMError extends Error {
  constructor(public reason: GuidedDenyReason | 'network' | 'unknown', message?: string) {
    super(message ?? reason);
    this.name = 'GuidedLLMError';
  }
}

export interface GuidedDeps {
  baseUrl: string;
  getToken: () => string | null;
  getDeviceId: () => string;
  onCredits?: (creditsRemaining: number) => void;
  onTier?: (tier: Tier) => void;
}

/** XHR with timeout disabled (timeout=0), so a long build that sends no bytes for
 *  60-90s isn't aborted by the platform's default request timeout. Falls back to
 *  fetch where XMLHttpRequest isn't available (node/tests). */
async function xhrRequest(url: string, method: string, headers: Record<string, string>, body?: string): Promise<{ status: number; text: string }> {
  if (typeof XMLHttpRequest === 'undefined') {
    const res: any = await fetch(url, { method, headers, body });
    const text = typeof res.text === 'function'
      ? await res.text().catch(() => '')
      : JSON.stringify(await res.json().catch(() => ({})));
    return { status: res.status, text };
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.timeout = 0; // no timeout — the build can take as long as it needs
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText ?? '' });
    xhr.onerror = () => reject(new GuidedLLMError('network', 'Network request failed'));
    xhr.onabort = () => reject(new GuidedLLMError('network', 'Request aborted'));
    xhr.send(body);
  });
}

export interface GenerateInput {
  systemPrompt: string;
  messages: { role: 'user' | 'assistant'; content: string }[];
  signedIn: boolean;
  firstBuild?: boolean;
  /** Needs schema-faithful output (a full AppDefinition or a patch) → routes to
   *  the capable build model with a high token cap, not the cheap chat model. */
  structured?: boolean;
  /** A data-URI/URL for a vision request (plan-first style extraction). Forces the
   *  capable vision model server-side. */
  image?: string;
}

export interface PopulateInput {
  intent: string;
  typeHint?: string;
  fields: { name: string; type?: string; options?: string[] }[];
  signedIn: boolean;
  source?: 'web' | 'knowledge';
}

export interface GuidedClient {
  generate(input: GenerateInput): Promise<{ content: string; creditsRemaining: number }>;
  populate(input: PopulateInput): Promise<{ records: Record<string, unknown>[]; creditsRemaining: number }>;
  /** Generate an image (Flux-schnell, persisted to S3) → a durable URL. `aspect` e.g. '16:9','1:1'. */
  generateImage(prompt: string, signedIn: boolean, aspect?: string, opts?: { sprite?: boolean }): Promise<{ url: string; creditsRemaining: number }>;
  syncCredits(signedIn: boolean): Promise<{ creditsRemaining: number; tier: Tier }>;
  /** Redeem a promo/credit code. Throws Error(reason) where reason is the server's
   *  code ('invalid_code' | 'already_redeemed' | 'signup_required') so the UI can map it. */
  redeem(code: string, signedIn: boolean): Promise<{ creditsRemaining: number; granted: number }>;
}

export function createGuidedClient(deps: GuidedDeps): GuidedClient {
  function authHeaders(signedIn: boolean): Record<string, string> {
    if (signedIn) {
      const token = deps.getToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    return { 'x-device-id': deps.getDeviceId() };
  }

  async function call<T>(path: string, method: string, signedIn: boolean, body?: unknown): Promise<T> {
    // A full build can poll Replicate server-side for 60-90s with no bytes sent to
    // the client. fetch maps to NSURLSession's ~60s request timeout, which would
    // abort a long build. XHR with timeout=0 disables that so the build can finish.
    const { status, text } = await xhrRequest(`${deps.baseUrl}${path}`, method, { 'Content-Type': 'application/json', ...authHeaders(signedIn) }, body ? JSON.stringify(body) : undefined);
    let data: unknown = {};
    try { data = JSON.parse(text); } catch { data = {}; }
    if (status < 200 || status >= 300) {
      const reason = (data as { error?: string })?.error;
      if (reason === 'signup_required' || reason === 'insufficient_credits' || reason === 'unavailable') {
        throw new GuidedLLMError(reason);
      }
      throw new GuidedLLMError('unknown', `Request failed (${status})`);
    }
    return data as T;
  }

  return {
    async generate(input) {
      const data = await call<{ content: string; creditsRemaining: number }>('/llm', 'POST', input.signedIn, {
        systemPrompt: input.systemPrompt,
        messages: input.messages,
        firstBuild: input.firstBuild ?? false,
        structured: input.structured ?? false,
        ...(input.image ? { image: input.image } : {}),
      });
      deps.onCredits?.(data.creditsRemaining);
      return data;
    },
    async populate(input) {
      const data = await call<{ records: Record<string, unknown>[]; creditsRemaining: number }>('/populate', 'POST', input.signedIn, {
        intent: input.intent, typeHint: input.typeHint, fields: input.fields, source: input.source,
      });
      deps.onCredits?.(data.creditsRemaining);
      return data;
    },
    async generateImage(prompt, signedIn, aspect, opts) {
      const data = await call<{ url: string; creditsRemaining: number }>('/image', 'POST', signedIn, { prompt, aspect, sprite: opts?.sprite });
      deps.onCredits?.(data.creditsRemaining);
      return data;
    },
    async syncCredits(signedIn) {
      const data = await call<{ creditsRemaining: number; tier: Tier }>('/credits', 'GET', signedIn);
      deps.onCredits?.(data.creditsRemaining);
      deps.onTier?.(data.tier);
      return data;
    },
    async redeem(code, signedIn) {
      // Direct request (not via `call`) so the exact server reason survives for the UI.
      const { status, text } = await xhrRequest(
        `${deps.baseUrl}/redeem`, 'POST',
        { 'Content-Type': 'application/json', ...authHeaders(signedIn) },
        JSON.stringify({ code }),
      );
      let data: { creditsRemaining?: number; granted?: number; tier?: Tier; error?: string } = {};
      try { data = JSON.parse(text); } catch { /* keep empty */ }
      if (status < 200 || status >= 300) {
        throw new Error(data.error || `Request failed (${status})`);
      }
      if (typeof data.creditsRemaining === 'number') deps.onCredits?.(data.creditsRemaining);
      // A code may also grant a subscription — reflect the new tier locally so paid
      // features (e.g. editing) unlock without waiting for the next /credits sync.
      if (data.tier) deps.onTier?.(data.tier);
      return { creditsRemaining: data.creditsRemaining ?? 0, granted: data.granted ?? 0 };
    },
  };
}
